import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../components/auth/AuthProvider';
import { ChatMessages } from '../components/ChatMessages';
import ChatInput from '../components/ChatInput';
import { MathJaxContext } from 'better-react-mathjax';
import { useTutorSession } from '../hooks/useTutorSession';
import { Robot, ArrowCounterclockwise } from 'react-bootstrap-icons';
import { getUserProfile } from '../utils/profileUtils';
import '../assets/styles/HomeworkHelp.css';
import { uploadAndProcessFile } from '../utils/fileUpload';
import { useBedrockClient } from '../context/BedrockClientContext';
import { useDynamoUtils } from '../context/DynamoUtilsContext';
import { useSession } from '../context/SessionContext';

// Template for enhanced system prompt with placeholders
const HOMEWORK_SYSTEM_PROMPT_TEMPLATE = `
You are a friendly, patient AI Maths Tutor whose goal is to help the student truly understand their homework—not just give answers. You are running a homework tutoring session with a student as part of a math turoring app, on a page called "Homework Help". The student has been invited to drop in a screenshot or type your question.

**SESSION START**
- Greet the student by name and, if they have sent their problem as an image, restate the problem to ensure you have it understood correctly for the user. If they have not sent a problem, be friendly and ask them to send a screenshot or type their question so you can help them.
- If a problem is provided, being with what a good first step would be to solve the problem, and see if the student can answer it. If they are stuck, ask them to describe what they think the problem is asking for, and what they have tried so far.

**TEACHING MODE**
Current mode: {{MODE_NAME}}

**PERSONALIZATION**
Student Name: {{STUDENT_NAME}}  
Year Level: Year {{YEAR_LEVEL}}  
Syllabus: {{TEXTBOOK_NAME}}  
Confidence: {{CONFIDENCE_SUMMARY}}  
Interests: {{INTERESTS}}  

**CONVERSATION STYLE**
- You goal is not to provide the entire solution to the student straight away, but to help the student understand the problem. Don't provide the entire solution immediately. Instead, break it down step by step and help the student to arrive at the answer themselves. Only provide the full solution if the student is really stuck and asks for it.
- Ask guiding questions to help the student arrive at the answer themselves.
- Use simple language and avoid jargon unless the student is familiar with it.
- Only ask one guiding question at a time, and ask follow up questions, or break the problem into smaller sub-steps if the student is stuck. You may also give hints rather than the full solution, or be creative in how you communicate the problem to the student.  Don't help students by fully answering their questions, but instead ask them to try to answer it themselves and guide them to the answer.
- Be encouraging and supportive to the student. Celebrate small wins and encourage them to verbalise their reasoning. 
- Show all your arithmetic step-by-step
- Use visual aids where appropriate, such as diagrams, graphs, or tables, to help explain concepts. You can use the tools available to you to create diagrams to explain concepts visually, or use ASCII sketches for simple ideas.
- As you go, adapt your teaching style to the student's cues.

**Formatting Rules:**
- Respond in Markdown.
- Format all math using LaTeX:
    - Inline: $ ... $
    - Display:
      $$
      ... multi-line LaTeX ...
      $$
- Do **not** use parentheses or backticks for math.
- Keep plain text and LaTeX separate.
- For tiny or linear ideas (number-lines, 2×2 tables, a single arrow, etc.)  
  → make a short **ASCII sketch** inside a fenced ''' block. Otherwise, use the tools available to you to create diagrams to explain concepts visually.

**OTHER**
- If you’re ever unsure or the problem statement is incomplete, ask for more context rather than guessing.
- You can currently only process images, not other formats like PDFs. If the student uploads a PDF, ask them to take a screenshot of the relevant page and upload that instead.

**Visual Diagramming Tools**

- plot_graph   → one explicit curve y = f(x)  
    args {expression, xRange? [-10,10], title?}

- draw_circle             → labelled circle/radius/diameter  
- draw_triangle           → labelled ΔABC (sides, angles)  
- draw_quadrilateral      → square / rectangle / parallelogram / trapezoid  
- draw_polygon_regular    → regular n-gon (5–10)  
    → all use shared args {…shape-specific…, title?}

Tool-use etiquette

**Etiquette**  
- Use **one tool OR one ASCII sketch per reply**, only when it aids learning.  
- Briefly explain the image you are going to provide, then after the image, add a short textual insight (don’t narrate every pixel).  
- Provide every *required* field, nothing more.  
- Avoid near-duplicate diagrams—wait for the learner to request changes.
- Numeric dimensions are interpreted as math units; the tutor will scale them so the diagram fills the canvas.

Tool examples:
{"name":"draw_circle","arguments":{
 "radius":5,"show_radius_line":true,"radius_label":"r",
 "show_diameter_line":true,"diameter_label":"d",
 "circumference_label":"C","title":"Circle parts"}}
{"name":"draw_triangle","arguments":{
 "vertex_A_label":"A","vertex_B_label":"B","vertex_C_label":"C",
 "side_AB_label":"c","side_BC_label":"a","side_CA_label":"b",
 "title":"Labelled ΔABC"}}
Rule of thumb: a purposeful visual boosts understanding; an unnecessary one distracts.
`;

const HomeworkHelp = () => {
  const auth = useAuth();
  const { user } = auth;
  const userId = user?.sub;
  const bedrockClientRef = useBedrockClient();
  const dynamoUtilsRef = useDynamoUtils();
  const { setSessionId: setGlobalSessionId } = useSession();
  const messagesEndRef = useRef(null);

  // UI state
  const [explanationLevel, setExplanationLevel] = useState('general');
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showFileUploadArea, setShowFileUploadArea] = useState(true);
  const [showWelcomeAnimation, setShowWelcomeAnimation] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [suggestedMessage, setSuggestedMessage] = useState('');
  const [fileProcessing, setFileProcessing] = useState(false);
  const [messageLimitReached, setMessageLimitReached] = useState(false);
  const [showMobileConfig, setShowMobileConfig] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    
    // Improved device detection
    const isPhone = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua);
    const isTablet = /ipad/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    // Only treat phones as mobile, not tablets
    return isPhone;
  });

  const [isTablet, setIsTablet] = useState(() => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    return /ipad/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  });
  
  // Advanced mobile optimizations
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [touchStartY, setTouchStartY] = useState(0);
  const [isScrollingUp, setIsScrollingUp] = useState(false);
  
  const MESSAGE_LIMIT = 25; // Maximum number of messages in a homework help chat

  const {
    messages,
    sessionId,
    isStreaming,
    loadConversation,
    sendMessage,
    stopGeneration,
    handleFileUpload,
    setMessages,
    createNewConversation,
    setSessionId,
    chatLimitReached,
    chatLimitInfo
  } = useTutorSession({
    auth,
    customTableName: 'ai-math-tutor-homework',
    sessionType: 'Homework Help',
  });

  useEffect(() => {
    if (sessionId) {
      setGlobalSessionId(sessionId);
    }
  }, [sessionId, setGlobalSessionId]);

  // Helper to sanitize fields
  const sanitize = str => str?.toString().replace(/\n/g, ' ').slice(0, 50) || '';

  // Detect mobile devices and update on resize/orientation
  useEffect(() => {
    const checkDeviceType = () => {
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      
      const isPhone = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua);
      const isTabletDevice = /ipad/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      
      setIsMobile(isPhone);
      setIsTablet(isTabletDevice);
    };

    checkDeviceType();
    window.addEventListener('resize', checkDeviceType);
    window.addEventListener('orientationchange', checkDeviceType);
    return () => {
      window.removeEventListener('resize', checkDeviceType);
      window.removeEventListener('orientationchange', checkDeviceType);
    };
  }, []);

  // Update CSS variable for viewport height to fix mobile 100vh issues
  useEffect(() => {
    // Only apply viewport height fixes on actual mobile devices
    if (!isMobile) return;
    
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setVh();
    window.addEventListener('resize', setVh);
    return () => window.removeEventListener('resize', setVh);
  }, [isMobile]);

  // Fetch session history function (moved up to fix dependency order)
  const fetchSessionHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    try {
      const dynamoUtils = dynamoUtilsRef.current;
      if (!dynamoUtils || !userId) return;
      const sessions = await dynamoUtils.listConversations(userId, 'homework', 'ai-math-tutor-homework');
      setSessionHistory(sessions || []);
    } catch (error) {
      console.error('Error fetching homework sessions:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [userId]);

  // Build the memoized system prompt by replacing placeholders
  const systemPrompt = useMemo(() => {
    const modeMap = {
      basics: 'GET THE BASICS',
      general: 'REVISION',
      extension: 'BECOME AN EXPERT'
    };
    const MODE_NAME = modeMap[explanationLevel] || modeMap.general;
    const STUDENT_NAME = sanitize(userProfile?.name) || 'Student';
    const YEAR_LEVEL = sanitize(userProfile?.yearLevel) || 'Unknown';
    const TEXTBOOK_NAME = sanitize(userProfile?.textbook) || 'General';
    const CONFIDENCE_SUMMARY =
      userProfile?.confidenceLevel === 'low'
        ? 'Struggles with math concepts'
        : userProfile?.confidenceLevel === 'high'
        ? 'Confident with most topics'
        : 'Understands most concepts but needs practice';
    const INTERESTS = sanitize(userProfile?.interests) || 'N/A';

    return HOMEWORK_SYSTEM_PROMPT_TEMPLATE
      .replace('{{MODE_NAME}}', MODE_NAME)
      .replace('{{STUDENT_NAME}}', STUDENT_NAME)
      .replace('{{YEAR_LEVEL}}', YEAR_LEVEL)
      .replace('{{TEXTBOOK_NAME}}', TEXTBOOK_NAME)
      .replace('{{CONFIDENCE_SUMMARY}}', CONFIDENCE_SUMMARY)
      .replace('{{INTERESTS}}', INTERESTS);
  }, [explanationLevel, userProfile]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Load session history
  useEffect(() => {
    if (userId) fetchSessionHistory();
  }, [userId, fetchSessionHistory]);

  // Load user profile
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const profile = await getUserProfile({ userId });
        setUserProfile(profile);
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    };
    if (userId) loadUserProfile();
  }, [userId]);

  // Advanced Mobile Optimizations - iOS Keyboard Detection and Viewport Handling
  useEffect(() => {
    if (!isMobile) return;

    const handleViewportChange = () => {
      const currentHeight = window.innerHeight;
      const heightDifference = keyboardHeight - currentHeight;
      
      // Detect keyboard visibility on iOS (significant height reduction)
      if (heightDifference > 150) {
        // Adjust chat container when keyboard is visible
        const chatView = document.querySelector('.chat-view');
        if (chatView) {
          chatView.style.paddingBottom = '0';
        }
      } else {
        // Reset padding when keyboard is hidden
        const chatView = document.querySelector('.chat-view');
        if (chatView) {
          chatView.style.paddingBottom = '';
        }
      }
      
      setKeyboardHeight(currentHeight);
    };

    // Enhanced touch handling for better scrolling performance
    const handleTouchStart = (e) => {
      setTouchStartY(e.touches[0].clientY);
      setIsScrollingUp(false);
    };

    const handleTouchMove = (e) => {
      if (!touchStartY) return;
      
      const touchY = e.touches[0].clientY;
      const deltaY = Math.abs(touchY - touchStartY);
      
      if (deltaY > 10) {
        setIsScrollingUp(touchY < touchStartY);
      }
    };

    const handleTouchEnd = () => {
      setTouchStartY(0);
      setTimeout(() => setIsScrollingUp(false), 100);
    };

    // Prevent zoom on double tap for input elements
    const preventZoom = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        e.preventDefault();
      }
    };

    // Add event listeners
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('touchstart', preventZoom);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchstart', preventZoom);
    };
  }, [isMobile, keyboardHeight, touchStartY]);

  // Performance optimization - Debounced scroll handling
  useEffect(() => {
    if (!isMobile) return;

    let scrollTimeout;
    const handleScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      
      scrollTimeout = setTimeout(() => {
        // Add any scroll-based optimizations here
        const messagesContainer = document.querySelector('.messages-container');
        if (messagesContainer && isScrollingUp) {
          // Enable hardware acceleration during scroll
          messagesContainer.style.transform = 'translateZ(0)';
          messagesContainer.style.willChange = 'scroll-position';
          
          setTimeout(() => {
            messagesContainer.style.transform = '';
            messagesContainer.style.willChange = '';
          }, 150);
        }
      }, 50);
    };

    const messagesContainer = document.querySelector('.messages-container');
    if (messagesContainer) {
      messagesContainer.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (messagesContainer) {
        messagesContainer.removeEventListener('scroll', handleScroll);
      }
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [isMobile, isScrollingUp]);


  // Send message handler
  const handleSendMessage = async text => {
    if (!text.trim()) return;
    setShowFileUploadArea(false);
    setShowWelcomeAnimation(false);
    
    // Check if assistant message limit has been reached
    const assistantCount = messages.filter(
      m =>
        m.role === 'assistant' &&
        !m.meta &&
        m.role !== 'tool' &&
        !['thinking', 'thinking_tool', 'executing', 'toolStart'].includes(m.status)
    ).length;
    if (assistantCount >= MESSAGE_LIMIT) {
      setMessageLimitReached(true);
      return;
    }
    
    // Log the system prompt that will be sent
    console.log('Sending system prompt:', systemPrompt);
    
    try {
      // If no sessionId exists, create a new conversation with a meta message first
      if (!sessionId) {
        // Now send the actual user message
        await sendMessage(text, systemPrompt, {
          type: 'homework',
          explanationLevel
        });
      }
      else {
      
        // Now send the actual user message
        await sendMessage(text, systemPrompt, {
          type: 'homework',
          explanationLevel
        });
      
        // Clear the suggested message after sending
        setSuggestedMessage('');
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // File upload handler
  const handleFileUploadSubmit = async fileData => {
    if (!fileData) return;
    setShowFileUploadArea(false);
    setShowWelcomeAnimation(false);
    try {
      // If no sessionId exists, create a new conversation with a meta message first
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        const studentName = userProfile?.name || 'Student';
        const currentTime = new Date().toLocaleString();
        const metaMessage = `
USER:
  - Name: ${studentName}
  - Confidence Level: ${userProfile?.confidenceLevel || 'medium'}
  - Interests: ${userProfile?.interests || 'N/A'}
SESSION: Homework help
Time: ${currentTime}
`.trim();
        
        // Create the conversation with the meta message and get the new sessionId
        currentSessionId = await createNewConversation(metaMessage, {
          type: 'homework',
          explanationLevel
        });
        
        // Short delay to ensure the conversation is created
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Now handle the file upload with the correct sessionId
      await handleFileUpload(fileData, { sessionId: currentSessionId });
      
      // Instead of automatically sending a message, set the suggested message
      // This will pre-populate the input field but allow the user to edit or send it
      setSuggestedMessage('Please help with this homework problem');
      
    } catch (error) {
      console.error('Error handling file upload:', error);
    }
  };

  // File upload handlers for the initial screen
  const handleFileUploadFromInitialScreen = async fileData => {
    if (!fileData) return;
    
    try {
      // If no sessionId exists, create a new conversation with a meta message first
      if (!sessionId) {
        const studentName = userProfile?.name || 'Student';
        const currentTime = new Date().toLocaleString();
        const metaMessage = `
USER:
  - Name: ${studentName}
  - Confidence Level: ${userProfile?.confidenceLevel || 'medium'}
  - Interests: ${userProfile?.interests || 'N/A'}
SESSION: Homework help
Time: ${currentTime}
`.trim();
        
        // Create the conversation with the meta message and store the new sessionId
        const newSessionId = await createNewConversation(metaMessage, {
          type: 'homework',
          explanationLevel
        });
        
        // Now handle the file upload - only after we have a valid session ID
        if (newSessionId) {
          // Pass the sessionId directly since the state might not be updated yet
          await handleFileUpload(fileData, { sessionId: newSessionId });
          
          // Set the suggested message for the chat input
          setSuggestedMessage('Please help with this homework problem');
          
          // Transition to chat view after file upload
          setShowFileUploadArea(false);
          setShowWelcomeAnimation(false);
        } else {
          console.error('Failed to create a new conversation before file upload');
        }
      } else {
        // We already have a session ID, just handle the file upload
        await handleFileUpload(fileData);
        setSuggestedMessage('Please help with this homework problem');
        setShowFileUploadArea(false);
        setShowWelcomeAnimation(false);
      }
    } catch (error) {
      console.error('Error handling file upload:', error);
    }
  };

  // File input change
  const handleFileChange = e => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = e => handleFileUploadFromInitialScreen(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Drag handlers
  const handleDrag = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };
  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      const file = e.dataTransfer.files[0];
      const reader = new FileReader();
      reader.onload = async () => {
        const dataUrl = reader.result;
        // now kick off the same upload flow as ChatInput does
        try {
          setFileProcessing(true);
          // 1 · ensure we *have* an ID and keep a local copy
          let currentSessionId = sessionId;
          if (!currentSessionId) {
            const studentName = userProfile?.name || 'Student';
            const meta = `
USER:
  - Name: ${studentName}
  - Confidence Level: ${userProfile?.confidenceLevel || 'medium'}
  - Interests: ${userProfile?.interests || 'N/A'}
SESSION: Homework help
Time: ${new Date().toLocaleString()}
`.trim();
            currentSessionId = await createNewConversation(meta, {
              type: 'homework',
              explanationLevel
            });
          }
          
          // 2 · upload & store the file
          const result = await uploadAndProcessFile(file, {
            userId,
            sessionId: currentSessionId,
            bedrockClient: bedrockClientRef.current,
            dynamoUtils: dynamoUtilsRef.current,
          });
          await handleFileUpload(result, { sessionId: currentSessionId, userId });
          // 4. then flip into chat-view & suggest the next message
          setShowFileUploadArea(false);
          setShowWelcomeAnimation(false);
          setSuggestedMessage('Please help with this homework problem');
          setFileProcessing(false);
        } catch (err) {
          console.error('Drag-and-drop upload error:', err);
          setFileProcessing(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Explanation level change
  const handleExplanationLevelChange = level => setExplanationLevel(level);

  // Reset page function
  const handleReset = () => {
    // Reset UI state
    setShowFileUploadArea(true);
    setShowWelcomeAnimation(true);
    
    // Reset message limit state
    setMessageLimitReached(false);
    
    // Close mobile config panel on reset
    if (isMobile) {
      setShowMobileConfig(false);
    }
    
    // Clear the messages without creating a new conversation in DynamoDB
    if (userId) {
      // Reset messages to empty array
      setMessages([]);
      
      // Reset session ID to null so a new one will be created on the next interaction
      setSessionId(null);
      
      // Clear any suggested message
      setSuggestedMessage('');
    }
  };

  // Toggle mobile config panel
  const toggleMobileConfig = () => {
    setShowMobileConfig(!showMobileConfig);
  };

  // Session selection
  const handleSelectSession = async id => {
    try {
      const sessionData = await loadConversation(id);
      setExplanationLevel(sessionData?.metadata?.explanationLevel || 'general');
      setShowFileUploadArea(false);
      setShowWelcomeAnimation(false);
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  // Render
  function formatNaturalDateTime(ts) {
    return new Date(ts).toLocaleString('en-US', {
      weekday: 'long',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  return (
    <MathJaxContext>
      <div className={`homework-help-page ${isMobile ? 'mobile' : 'desktop'}`}>
        <div className="homework-content">
          {/* Desktop Sidebar - Hidden on Mobile */}
          {!isMobile && (
            <div className="homework-sidebar">
              <div className="sidebar-title">
                <h2>Homework Help</h2>
              </div>
              
              <div className="reset-button-container">
                <button 
                  className="reset-button"
                  onClick={handleReset}
                >
                  <ArrowCounterclockwise className="reset-icon" />
                  Reset
                </button>
              </div>
              
              <div className="config-panel">
                <div className="explanation-level">
                  <label>Explanation Level:</label>
                  <div className="level-options">
                    <label className={`level-option ${explanationLevel === 'basics' ? 'active' : ''}`}>
                      <input 
                        type="radio" 
                        name="explanation-level" 
                        value="basics"
                        checked={explanationLevel === 'basics'}
                        onChange={() => handleExplanationLevelChange('basics')}
                      />
                      <span className="option-title">Get the Basics</span>
                    </label>
                    
                    <label className={`level-option ${explanationLevel === 'general' ? 'active' : ''}`}>
                      <input 
                        type="radio" 
                        name="explanation-level" 
                        value="general"
                        checked={explanationLevel === 'general'}
                        onChange={() => handleExplanationLevelChange('general')}
                      />
                      <span className="option-title">Revision</span>
                    </label>
                    
                    <label className={`level-option ${explanationLevel === 'extension' ? 'active' : ''}`}>
                      <input 
                        type="radio" 
                        name="explanation-level" 
                        value="extension"
                        checked={explanationLevel === 'extension'}
                        onChange={() => handleExplanationLevelChange('extension')}
                      />
                      <span className="option-title">Become an Expert</span>
                    </label>
                  </div>
                </div>
                {user && user.isPro && (
                  <div className="voice-option">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={voiceEnabled}
                        onChange={(e) => setVoiceEnabled(e.target.checked)}
                      />
                      Enable voice (Pro feature)
                    </label>
                  </div>
                )}
              </div>
              
              <div className="history-panel">
                <h3>Session History</h3>
                {isLoadingHistory ? (
                  <div className="loading">Loading history...</div>
                ) : sessionHistory.length === 0 ? (
                  <div className="empty-history">No previous sessions</div>
                ) : (
                  <div className="session-list">
                    {sessionHistory.map(session => {
                      const firstMessage = session.messages && session.messages.length > 0
                        ? session.messages[0].content.slice(0, 50)
                        : null;
                      const sessionTitle = firstMessage
                        ? firstMessage
                        : formatNaturalDateTime(session.timestamp);
                      
                      return (
                        <div 
                          key={session.conversation_id}
                          className="session-item"
                          onClick={() => handleSelectSession(session.conversation_id)}
                        >
                          <div className="session-title">
                            {sessionTitle}
                          </div>
                          <div className="session-date">
                            Homework Help
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <button 
                  className="refresh-history-btn"
                  onClick={fetchSessionHistory}
                >
                  Refresh History
                </button>
              </div>
            </div>
          )}

          <div className="chat-main-area">
            {showFileUploadArea ? (
              <div className={`initial-view ${showWelcomeAnimation ? 'welcome-animation' : ''}`}>
                <div className="welcome-message-container">
                  <div className="ai-tutor-message-card">
                    <div className="ai-tutor-identity">
                      <span className="robot-icon-container">
                        <Robot style={{ color: 'var(--primary-color)', width: '40px', height: '40px' }}/>
                      </span>
                      <div className="ai-tutor-greeting">
                        <h2 className="greeting-heading">Need homework help?</h2>
                        <p className="greeting-subtext">Drop in a screenshot or type your question.</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div 
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className={dragActive ? "combined-input-area drag-active" : "combined-input-area"}
                >
                  <div className="combined-input-content">
                    <div className="question-input">
                      <ChatInput
                        isStreaming={isStreaming}
                        onSend={handleSendMessage}
                        onFileUpload={handleFileUploadFromInitialScreen}
                        handleStopGeneration={stopGeneration}
                        disabled={!userId || chatLimitReached}
                        placeholder="Type your math homework question here or drag and drop an image..."
                        rows={5}
                        externalBusy={fileProcessing}
                        chatLimitReached={chatLimitReached}
                        chatLimitInfo={chatLimitInfo}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="disclaimer-notes">
                  <ul>
                    <li>Homework chats are limited to {MESSAGE_LIMIT} tutor responses each. Start a new chat to continue the conversation.</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="chat-view">
                {/* Mobile FAB - Only show in chat view on mobile */}
                {isMobile && (
                  <button 
                    className="mobile-fab"
                    onClick={toggleMobileConfig}
                  >
                    <ArrowCounterclockwise />
                  </button>
                )}
                
                <div className="messages-container">
                  <ChatMessages messages={messages} />
                  {messageLimitReached && (
                    <div className="message-limit-notification">
                      <div className="limit-notification-content">
                        <h3>Message Limit Reached</h3>
                        <p>You've reached the limit of {MESSAGE_LIMIT} tutor responses for this chat session.</p>
                        <p>Please start a new chat to continue getting homework help.</p>
                        <button 
                          className="reset-button"
                          onClick={handleReset}
                        >
                          <ArrowCounterclockwise className="reset-icon" />
                          Start New Chat
                        </button>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
                <div className="chat-input-area">
                  <ChatInput
                    isStreaming={isStreaming}
                    onSend={handleSendMessage}
                    onFileUpload={handleFileUploadSubmit}
                    handleStopGeneration={stopGeneration}
                    disabled={!userId || chatLimitReached}
                    placeholder="Type your follow-up question..."
                    setInitialMessage={suggestedMessage}
                    externalBusy={fileProcessing}
                    chatLimitReached={chatLimitReached}
                    chatLimitInfo={chatLimitInfo}
                  />
                </div>
              </div>
            )}

            {/* Mobile Bottom Sheet Configuration Panel */}
            {isMobile && showMobileConfig && (
              <div className="mobile-bottom-sheet-overlay" onClick={() => setShowMobileConfig(false)}>
                <div className="mobile-bottom-sheet" onClick={(e) => e.stopPropagation()}>
                  <div className="mobile-bottom-sheet-header">
                    <h3>Settings</h3>
                    <button 
                      className="close-bottom-sheet"
                      onClick={() => setShowMobileConfig(false)}
                    >
                      ✕
                    </button>
                  </div>
                  
                  <div className="mobile-bottom-sheet-content">
                    <div className="mobile-reset-section">
                      <button 
                        className="mobile-reset-button"
                        onClick={handleReset}
                      >
                        <ArrowCounterclockwise className="reset-icon" />
                        Start New Chat
                      </button>
                    </div>
                    
                    <div className="mobile-config-section">
                      <h4>Explanation Level</h4>
                      <div className="mobile-level-options">
                        <label className={`mobile-level-option ${explanationLevel === 'basics' ? 'active' : ''}`}>
                          <input 
                            type="radio" 
                            name="mobile-explanation-level" 
                            value="basics"
                            checked={explanationLevel === 'basics'}
                            onChange={() => handleExplanationLevelChange('basics')}
                          />
                          <span className="option-title">Get the Basics</span>
                        </label>
                        
                        <label className={`mobile-level-option ${explanationLevel === 'general' ? 'active' : ''}`}>
                          <input 
                            type="radio" 
                            name="mobile-explanation-level" 
                            value="general"
                            checked={explanationLevel === 'general'}
                            onChange={() => handleExplanationLevelChange('general')}
                          />
                          <span className="option-title">Revision</span>
                        </label>
                        
                        <label className={`mobile-level-option ${explanationLevel === 'extension' ? 'active' : ''}`}>
                          <input 
                            type="radio" 
                            name="mobile-explanation-level" 
                            value="extension"
                            checked={explanationLevel === 'extension'}
                            onChange={() => handleExplanationLevelChange('extension')}
                          />
                          <span className="option-title">Become an Expert</span>
                        </label>
                      </div>
                    </div>

                    {user && user.isPro && (
                      <div className="mobile-voice-section">
                        <h4>Voice Options</h4>
                        <label className="mobile-voice-option">
                          <input 
                            type="checkbox" 
                            checked={voiceEnabled}
                            onChange={(e) => setVoiceEnabled(e.target.checked)}
                          />
                          <span>Enable voice (Pro feature)</span>
                        </label>
                      </div>
                    )}
                    
                    <div className="mobile-history-section">
                      <h4>Session History</h4>
                      {isLoadingHistory ? (
                        <div className="loading">Loading history...</div>
                      ) : sessionHistory.length === 0 ? (
                        <div className="empty-history">No previous sessions</div>
                      ) : (
                        <div className="mobile-session-list">
                          {sessionHistory.slice(0, 5).map(session => {
                            const firstMessage = session.messages && session.messages.length > 0
                              ? session.messages[0].content.slice(0, 40)
                              : null;
                            const sessionTitle = firstMessage
                              ? firstMessage
                              : formatNaturalDateTime(session.timestamp);
                            
                            return (
                              <div 
                                key={session.conversation_id}
                                className="mobile-session-item"
                                onClick={() => {
                                  handleSelectSession(session.conversation_id);
                                  setShowMobileConfig(false);
                                }}
                              >
                                <div className="mobile-session-title">
                                  {sessionTitle}
                                </div>
                                <div className="mobile-session-date">
                                  {formatNaturalDateTime(session.timestamp)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <button 
                        className="mobile-refresh-history-btn"
                        onClick={fetchSessionHistory}
                      >
                        Refresh History
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </MathJaxContext>
  );
};

export default HomeworkHelp;
